import {
    useMemo,
} from 'react'
import {
    useSkyNetNavigate,
} from 'app/SkyNetRoutes/index'
import {
    CommonRoutes,
} from 'app/enums/common.enums'
import {
    createRoutesConfig,
} from 'app/SkyNetRoutes/skyNetRoutes.utils'
import useCreateSkyNetDomainRoutes, {
    SkyNetDomainSubRoutes,
} from 'app/shared-components/SkyNetDomain/useCreateSkyNetDomainRoutes'
import {
    SkyNetRoutesConfig,
} from 'app/SkyNetRoutes/SkyNetRoutes.types'
import {
    createChildEditRoutes,
} from 'app/shared-components/SkyNetDomain'

import {
    App,
    OrderManagementDomains,
    CustomerTransportTabs,
    InternalTransportTabs,
    LongTermLeaseTabs,
    LanesTabs,
    InternalTransportDomainsRoutes, LanesDomainRoutes,
} from 'app/enums/domain.enums'

export default function useOrderManagementRoutes() {
    const skyNetNavigate = useSkyNetNavigate()
    const createSkyNetDomainRoutes = useCreateSkyNetDomainRoutes()

    return useMemo(() => {
        const SkyNetDomainRoutes = createSkyNetDomainRoutes()
        const config: SkyNetRoutesConfig = {
            path: [App.OrderManagement],
            DailyOps: {
                route: OrderManagementDomains.DailyOps,
            },
            Pooling: {
                route: OrderManagementDomains.Pooling,
            },
            OpenBookings: {
                route: OrderManagementDomains.OpenBookings,
            },
            Lanes: {
                route: OrderManagementDomains.Lanes,
                ...createChildEditRoutes(
                    createSkyNetDomainRoutes(LanesDomainRoutes),
                )({
                    Overview: CommonRoutes.Overview,
                    LaneImplementation: LanesTabs.LaneImplementation,
                    Notifications: LanesTabs.Notifications,
                }),
            },
            CustomerOrders: {
                route: OrderManagementDomains.CustomerOrders,
                Overview: {
                    route: CommonRoutes.Overview,
                    via: SkyNetDomainRoutes.All.Edit,
                },
                Update: {
                    route: CommonRoutes.DataUpdate,
                    via: SkyNetDomainRoutes.All.Edit,
                },
                Notifications: {
                    route: CustomerTransportTabs.Notifications,
                    via: SkyNetDomainRoutes.All.Edit,
                },
                OrderChanges: {
                    route: CustomerTransportTabs.OrderChanges,
                    via: SkyNetDomainRoutes.All.Edit,
                },
                ContainerSelection: {
                    route: CustomerTransportTabs.ContainerSelection,
                    via: SkyNetDomainRoutes.All.Edit,
                },
                OrderSteps: {
                    route: CustomerTransportTabs.OrderSteps,
                    via: SkyNetDomainRoutes.All.Edit,
                },
                BillingInformation: {
                    route: CustomerTransportTabs.BillingInformation,
                    via: SkyNetDomainRoutes.All.Edit,
                },
                Invoices: {
                    route: CustomerTransportTabs.Invoices,
                    via: SkyNetDomainRoutes.All.Edit,
                },
                SharedPositioning: {
                    route: CustomerTransportTabs.SharedPositioning,
                    via: SkyNetDomainRoutes.All.Edit,
                },
                OrderConfirmation: {
                    route: CustomerTransportTabs.OrderConfirmation,
                    via: SkyNetDomainRoutes.All.Edit,
                },
                ChangeRequestApproval: {
                    route: CustomerTransportTabs.ChangeRequestApproval,
                    via: SkyNetDomainRoutes.All.Edit,
                },
                CreateAirlineLease: {
                    route: OrderManagementDomains.CreateAirlineLease,
                },
                CreateDirectLease: {
                    route: OrderManagementDomains.CreateDirectLease,
                },
            },
            InternalOrders: {
                route: OrderManagementDomains.InternalOrders,
                ...createChildEditRoutes(
                    createSkyNetDomainRoutes({
                        ...InternalTransportDomainsRoutes,
                    }),
                )({
                    Overview: CommonRoutes.Overview,
                    Notifications: InternalTransportTabs.Notifications,
                    ContainerSelection: InternalTransportTabs.ContainerSelection,
                    OrderSteps: InternalTransportTabs.OrderSteps,
                    ChangeHistory: CommonRoutes.ChangeHistory,
                }),
                Overview: {
                    route: CommonRoutes.Overview,
                    via: SkyNetDomainRoutes.All.Edit,
                },
                Notifications: {
                    route: InternalTransportTabs.Notifications,
                    via: SkyNetDomainRoutes.All.Edit,
                },
                ContainerSelection: {
                    route: InternalTransportTabs.ContainerSelection,
                    via: SkyNetDomainRoutes.All.Edit,
                },
                OrderSteps: {
                    route: InternalTransportTabs.OrderSteps,
                    via: SkyNetDomainRoutes.All.Edit,
                },
                ChangeHistory: {
                    route: CommonRoutes.ChangeHistory,
                    via: SkyNetDomainRoutes.All.Edit,
                },
                Create: {
                    route: SkyNetDomainSubRoutes.CREATE,
                },
            },
            FreightForwarders: {
                route: OrderManagementDomains.FreightForwarders,
                Overview: {
                    route: CommonRoutes.Overview,
                    via: SkyNetDomainRoutes.All.Edit,
                },
            },
            LongTermLease: {
                route: OrderManagementDomains.LongTermLease,
                Overview: {
                    route: CommonRoutes.Overview,
                    via: SkyNetDomainRoutes.All.Edit,
                },
                Update: {
                    route: CommonRoutes.DataUpdate,
                    via: SkyNetDomainRoutes.All.Edit,
                },
                Containers: {
                    route: LongTermLeaseTabs.Containers,
                    via: SkyNetDomainRoutes.All.Edit,
                },
                RelatedOrders: {
                    route: LongTermLeaseTabs.RelatedOrders,
                    via: SkyNetDomainRoutes.All.Edit,
                },
                ContractChangeHistory: {
                    route: LongTermLeaseTabs.ContractChangeHistory,
                    via: SkyNetDomainRoutes.All.Edit,
                },
                Create: {
                    route: SkyNetDomainSubRoutes.CREATE,
                },
            },
            PreBookings: {
                route: OrderManagementDomains.PreBookings,
                Overview: {
                    route: CommonRoutes.Overview,
                    via: SkyNetDomainRoutes.All.Edit,
                },
                Update: {
                    route: CommonRoutes.DataUpdate,
                    via: SkyNetDomainRoutes.All.Edit,
                },
                ChangeHistory: {
                    route: CommonRoutes.ChangeHistory,
                    via: SkyNetDomainRoutes.All.Edit,
                },
            },
            ProformaInvoices: {
                route: OrderManagementDomains.ProformaInvoices,
                Overview: {
                    route: CommonRoutes.Overview,
                    via: SkyNetDomainRoutes.All.Edit,
                },
                Update: {
                    route: CommonRoutes.DataUpdate,
                    via: SkyNetDomainRoutes.All.Edit,
                },
            },
        }

        return createRoutesConfig(skyNetNavigate)(config)
    }, [
        createSkyNetDomainRoutes,
        skyNetNavigate,
    ])
}
